import { ReactElement } from "react";
import { createRoot, Root } from "react-dom/client";
import {
  Classes,
  ContextMenuPopoverProps,
  hideContextMenu as hideBlueprintContextMenu,
  showContextMenu as showBlueprintContextMenu,
} from "@blueprintjs/core";

/**
 * Императивное контекстное меню Blueprint под React 18 — единственная точка вызова.
 *
 * `showContextMenu` из Blueprint 5 по умолчанию рисует меню через `ReactDOM.render`
 * и убирает через `unmountComponentAtNode`. В React 18 это устаревший режим, и dev-сборка
 * пишет предупреждение на каждый показ. Функцию рендера Blueprint даёт подменить на
 * `createRoot`, а корень мы запоминаем по контейнеру — иначе `hideContextMenu` нечем было бы
 * его размонтировать.
 */
const roots = new WeakMap<Element | DocumentFragment, Root>();

const unmount = (container: Element | DocumentFragment) => {
  roots.get(container)?.unmount();
  roots.delete(container);
};

const render = (element: ReactElement<ContextMenuPopoverProps>, container: Element | DocumentFragment) => {
  const root = createRoot(container);
  roots.set(container, root);
  root.render(element);

  return () => unmount(container);
};

/**
 * Фокус в открывающееся меню — без него не работает Esc.
 *
 * Esc меню ловит обработчиком на контейнере оверлея, то есть только когда фокус внутри.
 * Переводит его туда сам Blueprint, но одной попыткой в ближайшем кадре после открытия.
 * Окно же лежит в портале, а портал попадает в DOM вторым проходом рендера, и в корне
 * `createRoot` этот проход к тому кадру не успевает: попытка пропадает, фокус остаётся
 * на `body`. Поэтому в начале анимации открытия, когда окно уже в DOM, делаем то же, что
 * Blueprint: ставим фокус на стартовую ловушку фокуса. Сам контейнер фокус не примет —
 * `tabIndex` у него нет.
 */
const focusInside = (node: HTMLElement) => {
  const overlay = node.closest<HTMLElement>(`.${Classes.OVERLAY}`);
  if (overlay && !overlay.contains(document.activeElement)) {
    overlay.querySelector<HTMLElement>(`.${Classes.OVERLAY_START_FOCUS_TRAP}`)?.focus({ preventScroll: true });
  }
};

export const showContextMenu = (props: Omit<ContextMenuPopoverProps, "isOpen">) =>
  showBlueprintContextMenu(
    {
      ...props,
      onOpening: (node: HTMLElement) => {
        focusInside(node);
        props.onOpening?.(node);
      },
    },
    { render }
  );

export const hideContextMenu = () => hideBlueprintContextMenu({ domUnmounter: unmount });
